/**
 * The wait a customer is told, as the server works it out (engine.Estimate): every
 * surface shows this and none works out its own. Only how it is said is here.
 */

/** engine.Estimate: the wait with its range and flags. */
export interface Estimate {
  /** Rounded up to the minute, at least a minute, at most two hours; 0 with noDesk. */
  waitSec: number
  /** A range for a long wait; absent for a short one. */
  lowSec?: number
  highSec?: number
  /** Nobody is signed in at a desk that serves the line: no wait is given. */
  noDesk?: boolean
  /** The wait runs past the branch's last closing time today. */
  pastClosing?: boolean
  /** live: today's pace decided it; configured: the services' usual length. */
  basis?: 'live' | 'configured'
}

/** engine.QueueEstimate: what a customer taking a standard ticket now would be told. */
export interface QueueEstimate extends Estimate {
  queueId: string
  position: number
}

/** engine.TicketEstimate: where one waiting ticket stands. */
export interface TicketEstimate extends Estimate {
  ticketId: string
  position: number
}

/** report.EstimateRow: one business day and queue (both absent on the total). Error is actual minus told. */
export interface EstimateRow {
  businessDate?: string
  queueId?: string
  queueCode?: string
  queueName?: string
  count: number
  medianErrorSec: number
  /** 0–100 */
  withinFiveMinPct: number
  /** Above nought, customers waited longer than told. */
  meanErrorSec: number
  under: number
  over: number
  /** Told from today's pace / from the usual length. */
  live: number
  configured: number
}

/** report.EstimateAccuracy */
export interface EstimateAccuracy {
  range: { from: string; to: string }
  total: EstimateRow
  rows: EstimateRow[]
}

/** Two hours: where the server's estimate stops. */
export const ESTIMATE_CAP_SEC = 2 * 60 * 60

/** How a wait is shown, before words. */
export type WaitShown =
  | { kind: 'none' }
  | { kind: 'noDesk' }
  | { kind: 'over' }
  | { kind: 'min'; m: number }
  | { kind: 'range'; low: number; high: number }

export function waitShown(e: Estimate | null | undefined): WaitShown {
  if (!e) return { kind: 'none' }
  if (e.noDesk) return { kind: 'noDesk' }
  if (e.waitSec >= ESTIMATE_CAP_SEC) return { kind: 'over' }
  if (e.lowSec !== undefined && e.highSec !== undefined && e.highSec > e.lowSec) {
    return { kind: 'range', low: Math.max(1, Math.round(e.lowSec / 60)), high: Math.round(e.highSec / 60) }
  }
  if (e.waitSec > 0) return { kind: 'min', m: Math.max(1, Math.round(e.waitSec / 60)) }
  return { kind: 'none' }
}

/** What a printed slip carries: the minutes (a range's low and high) and the late flag. */
export function slipWait(e: Estimate | null | undefined): { waitMin?: number; waitMaxMin?: number; late?: boolean } {
  const w = waitShown(e)
  const late = e?.pastClosing && w.kind !== 'none' && w.kind !== 'noDesk' ? { late: true } : {}
  switch (w.kind) {
    case 'over':
      return { waitMin: ESTIMATE_CAP_SEC / 60, ...late }
    case 'min':
      return { waitMin: w.m, ...late }
    case 'range':
      return { waitMin: w.low, waitMaxMin: w.high, ...late }
    default:
      return {}
  }
}

/** The words a wait is said in, in whichever language the surface speaks. */
export interface WaitWords {
  /** "~{m} min" */
  min: (m: number) => string
  /** "~{low}–{high} min" */
  range: (low: number, high: number) => string
  /** "over 2 hours" */
  over: string
}

/** The wait in words: '—' with no desk, '' with no estimate. */
export function waitText(e: Estimate | null | undefined, words: WaitWords): string {
  const w = waitShown(e)
  switch (w.kind) {
    case 'noDesk':
      return '—'
    case 'over':
      return words.over
    case 'min':
      return words.min(w.m)
    case 'range':
      return words.range(w.low, w.high)
    default:
      return ''
  }
}

/** Whether to say "may not be served today": only beside a wait that was given. */
export function mayNotBeServed(e: Estimate | null | undefined): boolean {
  return !!e?.pastClosing && !e.noDesk && e.waitSec > 0
}

/** Each queue's estimate by queue id. */
export function byQueue(list: readonly QueueEstimate[] | null | undefined): Map<string, QueueEstimate> {
  return new Map((list ?? []).map((q) => [q.queueId, q]))
}

/** Each waiting ticket's estimate by ticket id. */
export function byTicket(list: readonly TicketEstimate[] | null | undefined): Map<string, TicketEstimate> {
  return new Map((list ?? []).map((t) => [t.ticketId, t]))
}
